import { memo, useCallback, useMemo, useRef, ReactElement } from 'react';
import { EditOutlined, EyeOutlined, FormOutlined, ProfileOutlined, PlusOutlined, UserAddOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, FirmGetAll } from '@pmp/api';
import { Guard, useGuard } from '@ohpen/react-authz';

import { columns } from '../tableConfig';
import { FirmsListProps } from '../interfaces';
import { lockActionTitle } from '@pmp/utils';

const entityType = ActionEntityType.LENDER_FIRMS;

export const FirmsList = memo(
  ({
    isFetching,
    data,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onOpenInPanel,
    onSeeFirmCases,
    addIntermediary,
    valueLists,
  }: FirmsListProps<FirmGetAll>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { t } = useTranslation();
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });

    const onAdd = useCallback(() => {
      onCreate();
    }, [onCreate]);

    const getRowActions: (record: FirmGetAll) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View firms" icon={<EyeOutlined rev={undefined} />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['firms:update']} flags={['enableFirmsCrud']}>
            <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit firms"
                icon={<EditOutlined key="edit" rev={undefined} />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Tooltip key="panel" title={t('lender.firms.labels.openInPanel')} placement="top">
            <Button
              type="text"
              aria-label="Open in Panel"
              icon={<FormOutlined key="edit" rev={undefined} />}
              onClick={() => onOpenInPanel(record)}
            />
          </Tooltip>,
          <Tooltip key="cases" title={t('lender.firms.labels.seeFirmCases')} placement="top">
            <Button
              type="text"
              aria-label="See Firm Cases"
              icon={<ProfileOutlined key="edit" rev={undefined} />}
              onClick={() => onSeeFirmCases(record)}
            />
          </Tooltip>,
          <Guard key="edit" permissions={['firms:update']} flags={['enableFirmsCrud']}>
            <Tooltip key="inviteIntermediary" title={t('lender.firms.labels.inviteIntermediary')} placement="top">
              <Button
                type="text"
                aria-label="See Firm Cases"
                icon={<UserAddOutlined key="edit" rev={undefined} />}
                onClick={() => addIntermediary(record)}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['firms:delete']} flags={['enableFirmsCrud']}>
            <Tooltip key="delete" title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete firms"
                icon={<DeleteOutlined key="delete" rev={undefined} />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];
        return rowActions;
      },
      [t, onView, onEdit, onOpenInPanel, onSeeFirmCases, addIntermediary, onDelete],
    );

    const tableProps: TableProps<ActionEntityType, FirmGetAll> = useMemo(
      () => ({
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns,
        rowKey: 'ohpenId',
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        loading: isFetching,
        showRowSelection,
        getRowActions,
        showViews: true,
        valueLists,
      }),
      [getRowActions, valueLists, data, isFetching, showRowSelection],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Table.ExportButton
                fileName={`Firms ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="Firms"
                disabled={isFetching}
                exportView
              />
              <Guard permissions={['firms:create']} flags={['enableFirmsCrud']}>
                <Button icon={<PlusOutlined />} onClick={onAdd} type="primary">
                  {t('global.buttons.addX', { x: t('lender.firms.labels.firm') })}
                </Button>
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="pmp.firms.labels.xFirmsSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                fileName={`Firms ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="Firms"
                disabled={isFetching}
                exportView
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </Table>
    );
  },
);
